import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Assessment, SubmissionScores, SectionScore, Answers, Lead } from '../types';
import { Blocks } from '../components/Blocks';
import ScoreProjector from '../components/ScoreProjector';
import { interpolateBlocks } from '../lib/interpolate';
import { beltAt } from '../lib/belt';
import { NewsletterOptIn } from '../components/NewsletterOptIn';
import { offersNewsletterOnEvent } from '../lib/newsletterOptIn';

const API = import.meta.env.VITE_API_BASE_URL as string;

interface Benchmarks {
  totalRespondents: number;
  communityAvgPct: number;
  sections: Record<string, { avgPct: number; avgScore: number; maxScore: number }>;
  ready: boolean; // false if not enough data
}

interface Props {
  assessment: Assessment;
  scores: SubmissionScores;
  submissionId?: string;
  answers?: Answers;
  preview?: boolean;
  /** The registrant's own name/email — used to pre-fill a booking CTA
   *  ({{bookingUrlPrefilled}}) so the native booking picker's email/name
   *  steps arrive already filled in. Never used to carry survey ANSWERS
   *  into a URL — see the HubSpot leak this deliberately avoids
   *  (docs/plans/summit-prep-survey-redesign.md §12). */
  lead?: Lead;
}

function scoreBand(score: number, max: number): { label: string; band: 'strong' | 'developing' | 'gap' } {
  const pct = max > 0 ? (score / max) * 100 : 0;
  if (pct >= 75) return { label: 'Strong', band: 'strong' };
  if (pct >= 42) return { label: 'Developing', band: 'developing' };
  return { label: 'Gap', band: 'gap' };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Result({ assessment, scores, submissionId, answers, preview, lead }: Props) {
  const content = assessment.config.results[scores.result];

  // Registration type: simple block-driven result page (confirmation or booking)
  if (assessment.type === 'registration') {
    // Build interpolation vars from eventConfig (eventUrl, bookingUrl, etc.)
    const vars: Record<string, string> = {};
    const ec = (assessment.config as any).eventConfig;
    if (ec && typeof ec === 'object') {
      for (const [k, v] of Object.entries(ec)) {
        if (typeof v === 'string') vars[k] = v;
      }
      // Flatten prepAsset onto the var bag so {{prepAssetUrl}}, {{prepAssetLabel}},
      // and {{prepAssetFileName}} resolve in result-block copy + CTA URLs.
      const pa = ec.prepAsset;
      if (pa && typeof pa === 'object') {
        if (typeof pa.url === 'string') vars.prepAssetUrl = pa.url;
        if (typeof pa.label === 'string') vars.prepAssetLabel = pa.label;
        if (typeof pa.fileName === 'string') vars.prepAssetFileName = pa.fileName;
      }
      // {{bookingUrlPrefilled}} — the same bookingUrl, carrying fn/ln/em query
      // params so the native booking picker (taker/src/BookingPage.tsx) arrives
      // with email/name already filled in — no retyping between "book with
      // Ron" and the calendar. NEVER put survey ANSWERS in this URL (that was
      // HubSpot's mistake — see sop-ee-summit-multi-event.md and
      // docs/plans/summit-prep-survey-redesign.md §12); only the identity
      // fields already captured at registration. Always defined when
      // bookingUrl is, even with no lead, so `{{bookingUrlPrefilled}}` never
      // resolves to an empty/broken link.
      if (typeof ec.bookingUrl === 'string' && ec.bookingUrl) {
        try {
          const isRelative = ec.bookingUrl.startsWith('/') && !ec.bookingUrl.startsWith('//');
          const u = new URL(ec.bookingUrl, isRelative ? window.location.origin : undefined);
          if (lead?.firstName) u.searchParams.set('fn', lead.firstName);
          if (lead?.lastName) u.searchParams.set('ln', lead.lastName);
          if (lead?.email) u.searchParams.set('em', lead.email);
          vars.bookingUrlPrefilled = isRelative ? u.pathname + u.search : u.toString();
        } catch {
          vars.bookingUrlPrefilled = ec.bookingUrl;
        }
      }
    }
    // Belt tokens ({{beltName}}, {{beltStage}}, {{beltCeilingName}}, {{beltGap}}).
    // scores.belt is null on every assessment that doesn't configure
    // beltScoring (see worker/src/scoring.ts scoreRegistration) — guarded so
    // this is a complete no-op everywhere except the belt-scored event.
    // beltAt() is the ONE accessor for the ladder (taker/src/lib/belt.ts) —
    // never add a second lookup table here, or the belt shown on this screen
    // can drift from the belt computed server-side.
    const belt = (scores as any).belt as
      | { belt: string; beltIndex: number; ceilingIndex: number; gap: number }
      | null
      | undefined;
    if (belt) {
      vars.beltName = beltAt(belt.beltIndex).name;
      vars.beltStage = beltAt(belt.beltIndex).stage;
      vars.beltCeilingName = beltAt(belt.ceilingIndex).name;
      vars.beltGap = String(belt.gap);
    }
    const interpolated = Object.keys(vars).length > 0 && content?.blocks
      ? interpolateBlocks(content.blocks, vars)
      : content?.blocks;

    const interpolateCta = (url?: string) =>
      url ? url.replace(/\{\{(\w+)\}\}/g, (_, k: string) => vars[k] ?? '') : url;

    // Auto-inject a prep-asset download card below the configured blocks when
    // an asset is uploaded. Always renders — duplication with hand-rolled config
    // would be obvious and easy for Lisa to spot in the admin preview.
    const prepAsset = ec?.prepAsset as { label?: string; fileName?: string; url?: string; sizeBytes?: number } | undefined;
    const prepAssetCard = prepAsset?.url ? (
      <div className="prep-asset-card">
        <div className="prep-asset-icon" aria-hidden="true">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <polyline points="9 15 12 18 15 15" />
          </svg>
        </div>
        <div className="prep-asset-body">
          <div className="prep-asset-eyebrow">Your prep materials</div>
          <div className="prep-asset-meta">
            {prepAsset.fileName ?? 'workbook'}
            {typeof prepAsset.sizeBytes === 'number' && prepAsset.sizeBytes > 0
              ? ` · ${formatBytes(prepAsset.sizeBytes)}`
              : ''}
          </div>
        </div>
        <a
          className="btn btn-primary btn-lg prep-asset-button"
          href={prepAsset.url}
          target="_blank"
          rel="noreferrer"
          download={prepAsset.fileName ?? true}
        >
          {prepAsset.label ?? 'Download Prep Workbook'}
        </a>
      </div>
    ) : null;

    return (
      <div className="result-page registration-result">
        {interpolated && <Blocks blocks={interpolated} />}
        {prepAssetCard}
        {offersNewsletterOnEvent(assessment.slug) && lead?.email && (
          <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>
            <NewsletterOptIn
              email={lead.email} firstName={lead.firstName ?? null} lastName={lead.lastName ?? null}
              source="registration" sourceDetail={assessment.slug} page="thank-you"
            />
          </div>
        )}
        {content?.cta && (
          <div className="registration-cta-zone">
            {(content.cta as any).primary && (
              <a className="btn btn-primary btn-xl" href={interpolateCta((content.cta as any).primary.url)} target="_blank" rel="noreferrer">
                {(content.cta as any).primary.label}
              </a>
            )}
            {(content.cta as any).secondary && (
              <a className="btn btn-secondary btn-lg" href={interpolateCta((content.cta as any).secondary.url)} target="_blank" rel="noreferrer">
                {(content.cta as any).secondary.label}
              </a>
            )}
          </div>
        )}
      </div>
    );
  }

  // surveyMode: a survey has no score, no pillars, no scorecard "book a call"
  // CTA — just a clean thank-you (the resolved tier's headline + body). Returned
  // here, alongside the registration branch, so the scorecard-only hooks below
  // never run for a survey.
  if (assessment.config.surveyMode) {
    return (
      <div className="result-page survey-result">
        <section className="result-hero-section">
          <div className="result-hero-inner" style={{ textAlign: 'center' }}>
            <h1 className="result-focus-name">{content?.headline ?? 'Thank you'}</h1>
            {content?.body && <p className="result-focus-copy">{content.body}</p>}
          </div>
        </section>
      </div>
    );
  }

  const sectionFocus = assessment.config.sectionFocusCopy ?? {};
  const sections = assessment.config.sections;

  // Fetch community benchmarks
  const [benchmarks, setBenchmarks] = useState<Benchmarks | null>(null);
  useEffect(() => {
    if (!assessment.slug) return;
    fetch(`${API}/api/benchmarks/${encodeURIComponent(assessment.slug)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBenchmarks(data); })
      .catch(() => {}); // silent fail — benchmarks are optional
  }, [assessment.slug]);

  const tierLabel = (() => {
    const t = assessment.config.scoring.tiers?.find((x) => x.key === scores.tier);
    return t?.label ?? content?.headline ?? scores.result;
  })();

  const primaryGap = Array.isArray(scores.primaryGap) ? scores.primaryGap[0] : scores.primaryGap;
  const primaryGapLabel = primaryGap
    ? sections.find((s) => s.id === primaryGap)?.label ?? primaryGap
    : undefined;
  const primaryGapCopy = primaryGap ? sectionFocus[primaryGap] : undefined;

  const gapScore = primaryGap && scores.sections ? scores.sections[primaryGap] : undefined;
  const gapPct = gapScore ? Math.round((gapScore.score / gapScore.max) * 100) : 0;

  const senderName = (assessment.config as any).emailFrom?.name
    || (assessment.config as any).signature?.name
    || 'our team';

  return (
    <div className="result-page">
      {/* ── 1. HERO: Focus Area + Score combined ── */}
      <section className="result-hero-section">
        <div className="result-hero-inner">

          {primaryGapLabel ? (
            <>
              <div className="result-focus-badge">Your #1 Focus Area</div>
              <h1 className="result-focus-name">{primaryGapLabel}</h1>
              {gapScore && (
                <div className="result-focus-score">
                  <span className="result-focus-pct">{gapPct}%</span>
                  <span className="result-focus-of">
                    {gapScore.score}/{gapScore.max} points
                    <span className="score-info-wrap">
                      <span className="score-info-icon" tabIndex={0}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                      </span>
                      <span className="score-info-tooltip">
                        Your {primaryGapLabel} score is {gapScore.score} out of {gapScore.max} possible points ({gapPct}%).
                        {gapPct < 42 ? ' This is your biggest growth opportunity — closing this gap can unlock significant revenue.' :
                         gapPct < 75 ? ' You\'re making progress here, but there\'s still room to strengthen this area.' :
                         ' You\'re strong here — keep building on this foundation.'}
                      </span>
                    </span>
                  </span>
                </div>
              )}
              <div className="result-overall-score">
                Overall: <span className="result-overall-number">{scores.total}</span><span className="result-overall-max">/{scores.maxTotal}</span>
                <span className="score-info-wrap">
                  <span className="score-info-icon" tabIndex={0}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </span>
                  <span className="score-info-tooltip">
                    Your overall score is {scores.total} out of {scores.maxTotal} across all {sections.length} pillars ({scores.maxTotal ? Math.round((scores.total ?? 0) / scores.maxTotal * 100) : 0}%).
                    {scores.maxTotal && (scores.total ?? 0) / scores.maxTotal >= 0.75 ? ' You\'re in a strong position — targeted coaching can take you from good to exceptional.' :
                     scores.maxTotal && (scores.total ?? 0) / scores.maxTotal >= 0.42 ? ' You have a solid foundation with clear gaps. A focused plan on your weakest pillar can shift the trajectory.' :
                     ' There\'s significant untapped potential in your business. The right system and guidance can close these gaps fast.'}
                  </span>
                </span>
              </div>
              {primaryGapCopy && <p className="result-focus-copy">{primaryGapCopy}</p>}
            </>
          ) : (
            <>
              <div className="result-compact-score">
                <span className="result-compact-number">{scores.total}</span>
                <span className="result-compact-max">/{scores.maxTotal}</span>
                <span className="score-info-wrap">
                  <span className="score-info-icon" tabIndex={0}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                  </span>
                  <span className="score-info-tooltip">
                    Your overall score is {scores.total} out of {scores.maxTotal} across all {sections.length} pillars ({scores.maxTotal ? Math.round((scores.total ?? 0) / scores.maxTotal * 100) : 0}%).
                  </span>
                </span>
              </div>
              <div className="result-tier-label">{tierLabel}</div>
            </>
          )}

          {content?.headline && <p className="result-headline-sub">{content.headline}</p>}
        </div>
      </section>

      {/* ── 2. Score Breakdown — dark bg, colored bars with community benchmarks ── */}
      {scores.type === 'scorecard' && scores.sections && (
        <section className="result-section">
          <div className="result-section-inner">
            <div className="result-scores-dark">
              <h3 className="result-scores-title">Your scores by pillar</h3>
              <div className="section-bars-dark">
                {Object.entries(scores.sections).map(([sid, s]: [string, SectionScore]) => {
                  const section = sections.find((x) => x.id === sid);
                  const pct = (s.score / s.max) * 100;
                  const { label: bandLabel, band } = scoreBand(s.score, s.max);
                  const communityPct = benchmarks?.ready && benchmarks.sections[sid]
                    ? benchmarks.sections[sid].avgPct : null;
                  return (
                    <div key={sid} className="sbar">
                      <div className="sbar-header">
                        <span className="sbar-name">{section?.label ?? sid}</span>
                        <span className={`sbar-badge ${band}`}>{bandLabel} · {s.score}/{s.max}</span>
                      </div>
                      <div className="sbar-track">
                        <div className={`sbar-fill ${band}`} style={{ width: `${pct}%` }} />
                        {communityPct !== null && (
                          <div className="sbar-community-marker" style={{ left: `${communityPct}%` }} title={`Community avg: ${Math.round(communityPct)}%`}>
                            <div className="sbar-community-line" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {benchmarks?.ready && (
                <div className="sbar-legend">
                  <span className="sbar-legend-item"><span className="sbar-legend-bar" /> Your score</span>
                  <span className="sbar-legend-item"><span className="sbar-legend-marker" /> Community avg ({benchmarks.totalRespondents} respondents)</span>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── 2b. Community Benchmarks or Coming Soon ── */}
      {scores.type === 'scorecard' && (
        <section className="result-section">
          <div className="result-section-inner narrow">
            {benchmarks?.ready ? (
              <div className="benchmark-card">
                <div className="benchmark-header">
                  <h3 className="benchmark-title">How you compare</h3>
                  <span className="benchmark-respondents">{benchmarks.totalRespondents} respondents</span>
                </div>
                <div className="benchmark-comparison">
                  <div className="benchmark-col yours">
                    <div className="benchmark-score">{scores.maxTotal ? Math.round(((scores.total ?? 0) / scores.maxTotal) * 100) : 0}%</div>
                    <div className="benchmark-label">Your score</div>
                  </div>
                  <div className="benchmark-vs">vs</div>
                  <div className="benchmark-col community">
                    <div className="benchmark-score">{Math.round(benchmarks.communityAvgPct)}%</div>
                    <div className="benchmark-label">Community avg</div>
                  </div>
                </div>
                {scores.maxTotal && (scores.total ?? 0) / scores.maxTotal * 100 > benchmarks.communityAvgPct ? (
                  <p className="benchmark-insight">You're scoring above the community average. You're closer to a breakthrough than most.</p>
                ) : (
                  <p className="benchmark-insight">You're not alone — most bookkeepers have the same gap. The difference is what you do next.</p>
                )}
              </div>
            ) : (
              <div className="benchmark-coming">
                <div className="benchmark-coming-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="12" width="4" height="9" rx="1" />
                    <rect x="10" y="8" width="4" height="13" rx="1" />
                    <rect x="17" y="3" width="4" height="18" rx="1" />
                  </svg>
                </div>
                <div className="benchmark-coming-body">
                  <div className="benchmark-coming-title">Community benchmarks coming soon</div>
                  <p className="benchmark-coming-text">
                    Once enough people have taken this scorecard, we'll email you how your scores compare
                    to the community — pillar by pillar. One more reason to check your inbox.
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── 2c. Score Projector — ROI Calculator ── */}
      {scores.type === 'scorecard' && scores.sections && (
        <section className="result-section">
          <div className="result-section-inner">
            <ScoreProjector
              scores={scores}
              sections={sections.filter(s => s.scored !== false)}
              answers={answers}
            />
          </div>
        </section>
      )}

      {/* ── 3. Validation — gut punch ── */}
      {content?.validation && (
        <section className="result-section dark">
          <div className="result-section-inner narrow">
            <p className="result-validation-text">{content.validation}</p>
          </div>
        </section>
      )}

      {/* ── 4. What This Means + Cost ── */}
      {content?.whatThisMeans && (
        <section className="result-section">
          <div className="result-section-inner narrow">
            <div className="card result-card">
              <h3 className="result-card-title">What this means for your business</h3>
              <p className="result-body-text">{content.whatThisMeans}</p>

              {scores.type === 'scorecard' && primaryGapLabel && (
                <div className="result-cost-box">
                  <div className="result-cost-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                    </svg>
                  </div>
                  <div>
                    <div className="result-cost-title">The cost of not closing this gap</div>
                    <p className="result-cost-text">
                      Every month your {primaryGapLabel.toLowerCase()} gap stays open, you're leaving $3,000–$7,000
                      in potential monthly revenue on the table. That's not theory — it's what bookkeepers
                      like you recover when they close this exact gap.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── 5. Transformation ── */}
      <section className="result-section dark">
        <div className="result-section-inner narrow">
          <div className="result-transform">
            <h2 className="result-transform-title">Close the gap. Change the trajectory.</h2>
            <div className="result-transform-grid">
              <div className="result-transform-col">
                <div className="result-transform-label before">Where you are now</div>
                <ul className="result-transform-list">
                  <li>Clients come in unpredictably</li>
                  <li>Marketing feels like guesswork</li>
                  <li>Prospects go quiet — and you're not sure why</li>
                  <li>Growth depends on luck</li>
                </ul>
              </div>
              <div className="result-transform-arrow">→</div>
              <div className="result-transform-col">
                <div className="result-transform-label after">Where you could be in 90 days</div>
                <ul className="result-transform-list highlight">
                  <li>Pipeline fills itself consistently</li>
                  <li>Right-fit prospects say yes — and mean it</li>
                  <li>The right clients find you, not the reverse</li>
                  <li>Growth runs on a system, not hope</li>
                </ul>
              </div>
            </div>

            <div className="result-proof-strip">
              <div className="result-proof-item">
                <div className="result-proof-stat">$250K+</div>
                <div className="result-proof-desc">Jennifer built her firm almost entirely on referrals</div>
              </div>
              <div className="result-proof-item">
                <div className="result-proof-stat">440%</div>
                <div className="result-proof-desc">Kristy's growth — coaching paid back in 2 months</div>
              </div>
              <div className="result-proof-item">
                <div className="result-proof-stat">70</div>
                <div className="result-proof-desc">Danielle's client count — zero marketing spend</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Custom blocks — styled as standout recommendation ── */}
      {content?.blocks && content.blocks.length > 0 && (
        <section className="result-recommendation-section">
          <div className="result-recommendation-inner">
            <div className="result-recommendation-glow" />
            <div className="result-recommendation-content">
              <div className="result-recommendation-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <Blocks blocks={content.blocks} />
            </div>
          </div>
        </section>
      )}

      {/* ── 6. THE CTA ── */}
      {!preview && <section className="result-cta-section">
        <div className="result-cta-inner">
          <div className="result-cta-eyebrow">Your next step</div>
          <h2 className="result-cta-headline">
            3 minutes ago, you didn't know where the money was stuck. Now you do.
          </h2>
          <p className="result-cta-body">
            The bookkeepers who turned their gap into growth didn't do it by guessing harder.
            They got a clear plan — specific to their weakest pillar — and closed it with guidance.
            That's what we help you do next.
          </p>

          <div className="result-cta-buttons">
            {content?.cta?.primary && (
              <a className="btn btn-primary btn-xl" href={content.cta.primary.url} target="_blank" rel="noreferrer">
                {content.cta.primary.label}
              </a>
            )}
            {content?.cta?.secondary && (
              <a className={`btn ${content?.cta?.primary ? 'btn-secondary' : 'btn-primary'} btn-lg`} href={content.cta.secondary.url} target="_blank" rel="noreferrer">
                {content.cta.secondary.label}
              </a>
            )}
          </div>

          <p className="result-cta-followup">
            {senderName} will also email you a personalised recommendation based on your score. Check your inbox.
          </p>

          <div className="result-cta-guarantee">
            <span className="result-cta-guarantee-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </span>
            <span>Free. No obligation. Just a conversation about your specific gap and how to close it.</span>
          </div>
        </div>
      </section>}

      {/* ── Footer ── */}
      <section className="result-footer">
        {content?.shareText && <p className="result-share">{content.shareText}</p>}
        {submissionId && (
          <>
            <p className="result-link">
              We're also emailing this link to you. Bookmark it to revisit anytime:{' '}
              <a href={`/results/${submissionId}`}>{`${location.origin}/results/${submissionId}`}</a>
            </p>
            <p className="result-answers-link">
              <Link to={`/answers/${submissionId}`}>See your answers</Link>
            </p>
          </>
        )}
      </section>
    </div>
  );
}
